import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Check, Copy, Download, ExternalLink, Mail, Pencil, Plus, Trash2 } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { AttendanceDetail } from "../components/AttendanceDetail";
import {
  AttendanceCorrectionHistory,
  AttendanceCorrections,
} from "../components/AttendanceCorrections";
import { Avatar, Badge, Button, Card, EmptyState, Skeleton, TableSkeleton } from "../components/ui";
import { formatDate, formatDateTime, formatDayCount, formatMoney } from "../lib/format";
import { structureTotals } from "../lib/salary";
import { AddStructure } from "./SalaryPage";
import { isAccountAdmin, isManagerOrAbove, reportingManagerState } from "../lib/people";
import { ATTENDANCE_LABEL, LEAVE_LABEL, LEAVE_TONE, SHIFT_LABEL, SHIFT_TONE, formatMinutes } from "../lib/people";
import type {
  DocumentCategory,
  DocumentVisibility,
  EmployeeDocument,
  EmployeeFull,
  LeaveBalance,
  LeaveRequest,
  PayrollItem,
  SalaryStructure,
  Shift,
  Skill,
} from "../api/types";

type TabId =
  | "overview"
  | "attendance"
  | "roster"
  | "leave"
  | "salary"
  | "payroll"
  | "documents"
  | "timeline";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "attendance", label: "Attendance" },
  { id: "roster", label: "Roster" },
  { id: "leave", label: "Leave" },
  { id: "salary", label: "Salary" },
  { id: "payroll", label: "Payroll" },
  { id: "documents", label: "Documents" },
  { id: "timeline", label: "Timeline" },
];

/**
 * How each invitation state reads to an owner. `accepted` is the only one that
 * is not a pending action, so any of the others offers resend/revoke.
 */
const INVITE_META: Record<
  string,
  { label: string; tone: "slate" | "emerald" | "amber" | "rose" }
> = {
  none: { label: "No login", tone: "slate" },
  pending: { label: "Invitation pending", tone: "amber" },
  accepted: { label: "Login active", tone: "emerald" },
  expired: { label: "Invitation expired", tone: "rose" },
  revoked: { label: "Invitation revoked", tone: "rose" },
  failed: { label: "Invitation failed to send", tone: "rose" },
};

const INVITE_ACTIONABLE = new Set(["pending", "expired", "revoked", "failed"]);

/**
 * The employee profile: one record, every surface that hangs off it.
 *
 * Tabs are chosen so a manager never has to leave the person to understand
 * them, and each tab reads the module that owns the data rather than
 * duplicating it.
 */
export function EmployeeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = isManagerOrAbove(user?.role);
  const canAdmin = isAccountAdmin(user?.role);
  const queryClient = useQueryClient();
  const timezone = user?.account?.timezone;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const successNotice =
    (location.state as { notice?: string } | null)?.notice ?? null;
  const [tab, setTab] = useState<TabId>(() => {
    const requested = searchParams.get("tab");
    return (TABS.some((item) => item.id === requested) ? requested : "overview") as TabId;
  });
  const [headerCopied, setHeaderCopied] = useState(false);

  const employee = useQuery({
    queryKey: ["employee", id],
    queryFn: ({ signal }) => api.employee(id!, signal),
  });

  // Whether this profile is the signed-in person's own record. Drives which
  // pay tabs are shown: salary is owner/admin-only, payslips are visible for
  // your own record (never a colleague's).
  const me = useQuery({
    queryKey: ["employee-me"],
    queryFn: ({ signal }) => api.employeeMe(signal),
    enabled: !canAdmin,
    retry: false,
  });
  const isSelf = !canAdmin && Boolean(me.data?.id) && me.data?.id === id;

  const visibleTabs = TABS.filter((item) => {
    if (item.id === "salary") return canAdmin;
    if (item.id === "payroll") return canAdmin || isSelf;
    return true;
  });

  const deactivate = useMutation({
    mutationFn: () => api.deactivateEmployee(id!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employee", id] }),
  });
  const reactivate = useMutation({
    mutationFn: () => api.reactivateEmployee(id!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employee", id] }),
  });
  const uploadPhoto = useMutation({
    mutationFn: (file: File) => api.uploadEmployeePhoto(id!, file),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employee", id] }),
  });
  const resendInvite = useMutation({
    mutationFn: () => api.resendEmployeeInvite(id!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employee", id] }),
  });
  const revokeInvite = useMutation({
    mutationFn: () => api.revokeEmployeeInvite(id!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["employee", id] }),
  });

  if (employee.isPending) {
    return (
      <Card>
        <div className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
      </Card>
    );
  }
  if (employee.isError) {
    const error = employee.error;
    return (
      <EmptyState
        title={error instanceof ApiError && error.isPlanLimit ? "Plan limit reached" : "Could not load employee"}
        body={error instanceof ApiError ? error.message : "Please try again."}
      />
    );
  }

  const record = employee.data;
  const exited = record.status === "exited";

  return (
    <>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Avatar
            name={record.display_name}
            src={record.photo_data || record.photo_url}
            size={64}
          />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">
              {record.display_name}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {record.employee_code} · {record.designation || "Employee"} ·{" "}
              {record.department_name || "No department"} ·{" "}
              {record.branch_name || "No branch"}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge tone={record.status === "active" ? "emerald" : "slate"}>
                {record.status?.replace(/_/g, " ")}
              </Badge>
              {record.has_login ? (
                <Badge tone={INVITE_META[record.invite_status]?.tone ?? "slate"}>
                  {INVITE_META[record.invite_status]?.label ?? "Login enabled"}
                </Badge>
              ) : (
                <span className="text-xs text-slate-400">No login</span>
              )}
              {canAdmin ? (
                <Link
                  to={`/people/employees/${record.id}/edit`}
                  aria-label={`Edit employee ${record.display_name}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  <Pencil size={14} /> Edit Employee
                </Link>
              ) : isSelf ? (
                <Link
                  to="/people/me/edit"
                  aria-label="Edit my profile"
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  <Pencil size={14} /> Edit My Profile
                </Link>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canAdmin && record.invitation_url ? (
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(record.invitation_url!);
                setHeaderCopied(true);
                setTimeout(() => setHeaderCopied(false), 2000);
              }}
            >
              {headerCopied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
              {headerCopied ? "Copied!" : "Copy invite link"}
            </Button>
          ) : null}
          {canAdmin && (record.invite_status === "none" || !record.has_login) ? (
            <Button
              variant="ghost"
              disabled={resendInvite.isPending}
              onClick={() => resendInvite.mutate()}
            >
              {resendInvite.isPending ? "Generating…" : "Generate invite"}
            </Button>
          ) : null}
          {canAdmin && INVITE_ACTIONABLE.has(record.invite_status) ? (
            <>
              <Button
                variant="ghost"
                disabled={resendInvite.isPending || revokeInvite.isPending}
                onClick={() => resendInvite.mutate()}
              >
                {resendInvite.isPending ? "Sending…" : "Resend invite"}
              </Button>
              {record.invite_status === "pending" ? (
                <Button
                  variant="ghost"
                  disabled={resendInvite.isPending || revokeInvite.isPending}
                  onClick={() => revokeInvite.mutate()}
                >
                  {revokeInvite.isPending ? "Revoking…" : "Revoke invite"}
                </Button>
              ) : null}
            </>
          ) : null}
          {canAdmin ? (
            <label className="cursor-pointer rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50">
              {uploadPhoto.isPending ? "Uploading…" : "Photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) uploadPhoto.mutate(file);
                  event.target.value = "";
                }}
              />
            </label>
          ) : null}
          {canAdmin ? (
            exited ? (
              <Button disabled={reactivate.isPending} onClick={() => reactivate.mutate()}>
                Reactivate
              </Button>
            ) : (
              <Button variant="ghost" disabled={deactivate.isPending} onClick={() => deactivate.mutate()}>
                Deactivate
              </Button>
            )
          ) : null}
        </div>
      </header>

      {successNotice ? (
        <p
          role="status"
          className="mb-5 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {successNotice}
        </p>
      ) : null}

      {(resendInvite.isError || revokeInvite.isError) && (
        <p role="alert" className="mb-5 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {(() => {
            const error = resendInvite.error ?? revokeInvite.error;
            return error instanceof ApiError ? error.message : "The invitation action failed.";
          })()}
        </p>
      )}

      <div className="mb-5 flex flex-wrap gap-1 border-b border-slate-200">
        {visibleTabs.map((item) => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={
              tab === item.id
                ? "-mb-px border-b-2 border-slate-900 px-3 py-2 text-sm font-medium text-slate-900"
                : "-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-800"
            }
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <OverviewTab
          record={record}
          timezone={timezone}
          canAdmin={canAdmin}
          onResend={() => resendInvite.mutate()}
          onRevoke={() => revokeInvite.mutate()}
          isResending={resendInvite.isPending}
          isRevoking={revokeInvite.isPending}
        />
      ) : null}
      {tab === "attendance" ? <AttendanceTab employeeId={record.id} timezone={timezone} /> : null}
      {tab === "roster" ? <RosterTab employeeId={record.id} /> : null}
      {tab === "leave" ? <LeaveTab employeeId={record.id} /> : null}
      {tab === "salary" ? (
        <SalaryTab employeeId={record.id} canRevise={canAdmin} currency={user?.account?.currency} />
      ) : null}
      {tab === "payroll" ? <PayrollTab employeeId={record.id} timezone={timezone} /> : null}
      {tab === "documents" ? (
        <DocumentsTab
          employeeId={record.id}
          canManage={canManage}
          canAdmin={canAdmin}
        />
      ) : null}
      {tab === "timeline" ? <TimelineTab record={record} timezone={timezone} /> : null}
    </>
  );
}

function InvitationCard({
  record,
  timezone,
  onResend,
  onRevoke,
  isResending,
  isRevoking,
}: {
  record: EmployeeFull;
  timezone?: string | null;
  onResend: () => void;
  onRevoke: () => void;
  isResending: boolean;
  isRevoking: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-slate-600" />
          <h2 className="text-sm font-semibold text-slate-900">Employee Invitation & Access</h2>
        </div>
        <Badge tone={INVITE_META[record.invite_status]?.tone ?? "slate"}>
          {INVITE_META[record.invite_status]?.label ?? "No login"}
        </Badge>
      </div>

      <div className="mt-4 space-y-3">
        {record.invitation_url ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">
              Individual Invitation URL (for password creation):
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                readOnly
                value={record.invitation_url}
                className="min-w-64 flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-800 select-all focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(record.invitation_url!)}
              >
                {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                {copied ? "Copied!" : "Copy URL"}
              </Button>
              <a
                href={record.invitation_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <ExternalLink size={13} />
                Open link
              </a>
            </div>
            {record.invite_expires_at ? (
              <p className="mt-1 text-xs text-slate-500">
                Expires: {formatDateTime(record.invite_expires_at, timezone ?? undefined)}
              </p>
            ) : null}
            <div className="mt-3 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={isResending || isRevoking}
                onClick={onResend}
              >
                {isResending ? "Generating…" : "Resend / Re-generate URL"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                disabled={isResending || isRevoking}
                onClick={onRevoke}
              >
                {isRevoking ? "Revoking…" : "Revoke invitation"}
              </Button>
            </div>
          </div>
        ) : record.invite_status === "revoked" ? (
          <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-800">
            <p className="font-medium">Invitation Revoked</p>
            <p className="mt-0.5">The previous invitation URL was revoked and cannot be used to register. You can generate a fresh link.</p>
            <div className="mt-2">
              <Button size="sm" disabled={isResending} onClick={onResend}>
                {isResending ? "Generating…" : "Generate new invitation URL"}
              </Button>
            </div>
          </div>
        ) : record.invite_status === "expired" ? (
          <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-800">
            <p className="font-medium">Invitation Expired</p>
            <p className="mt-0.5">The invitation URL has expired. Re-issue an invitation link to allow the employee to register.</p>
            <div className="mt-2">
              <Button size="sm" disabled={isResending} onClick={onResend}>
                {isResending ? "Sending…" : "Resend invitation"}
              </Button>
            </div>
          </div>
        ) : record.invite_status === "accepted" ? (
          <div className="rounded-md bg-emerald-50 p-3 text-xs text-emerald-800">
            <p className="font-medium">Account Active</p>
            <p className="mt-0.5">Employee has been saved, login created.</p>
          </div>
        ) : (
          <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700">
            <p className="font-medium">No invitation link</p>
            <p className="mt-0.5">This employee does not have an invitation link yet. Click below to generate an individual secure invitation URL.</p>
            <div className="mt-2">
              <Button size="sm" disabled={isResending} onClick={onResend}>
                {isResending ? "Generating…" : "Generate invitation URL"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function OverviewTab({
  record,
  timezone,
  canAdmin,
  onResend,
  onRevoke,
  isResending,
  isRevoking,
}: {
  record: EmployeeFull;
  timezone?: string | null;
  canAdmin: boolean;
  onResend: () => void;
  onRevoke: () => void;
  isResending: boolean;
  isRevoking: boolean;
}) {
  const skills = Array.isArray(record.skills) ? (record.skills as Skill[]) : [];
  const tags = Array.isArray(record.tags) ? (record.tags as string[]) : [];
  const certifications = Array.isArray(record.certifications) ? (record.certifications as string[]) : [];

  // Financial lives on the employee as a bank reference and a current salary
  // structure. Salary is a company-management fact, so only an owner/admin
  // reads it here -- a manager sees the employment shape, not the pay.
  const salary = useQuery({
    queryKey: ["employee-salary", record.id],
    queryFn: ({ signal }) => api.salaryStructures({ employee: record.id, page_size: 5 }, signal),
    enabled: canAdmin,
  });
  const currentSalary = (salary.data?.results ?? []).find((s) => s.is_current) ?? salary.data?.results?.[0];

  return (
    <>
      {canAdmin ? (
        <InvitationCard
          record={record}
          timezone={timezone}
          onResend={onResend}
          onRevoke={onRevoke}
          isResending={isResending}
          isRevoking={isRevoking}
        />
      ) : null}
      <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Personal</h2>
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Row label="Full name" value={record.display_name} />
          <Row label="Gender" value={record.gender?.replace(/_/g, " ")} capitalize />
          <Row label="Date of birth" value={formatDate(record.date_of_birth, timezone ?? undefined)} />
          <Row label="Phone" value={record.phone} />
          <Row label="Email" value={record.email} />
          <Row label="Address" value={record.address} />
          <Row label="Emergency contact" value={record.emergency_contact_name} />
          <Row label="Emergency phone" value={record.emergency_contact_phone} />
        </dl>
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Employment</h2>
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Row label="Employee ID" value={record.employee_code} />
          <Row label="Employment type" value={record.employment_type?.replace(/_/g, " ")} capitalize />
          <Row label="Designation" value={record.designation} />
          <Row label="Department" value={record.department_name} />
          <Row label="Branch" value={record.branch_name} />
          <Row
            label="Reports to"
            value={
              record.reporting_to_name ||
              (record.is_account_owner ? "Owner — reports to no one" : "")
            }
          />
          <Row label="Joining date" value={formatDate(record.joining_date, timezone ?? undefined)} />
          <Row label="Probation end" value={formatDate(record.probation_end_date, timezone ?? undefined)} />
          <Row label="Confirmation date" value={formatDate(record.confirmation_date, timezone ?? undefined)} />
          <Row label="Notice period" value={record.notice_period_days ? `${record.notice_period_days} days` : ""} />
          <Row label="Resignation date" value={formatDate(record.resignation_date, timezone ?? undefined)} />
          <Row label="Last working day" value={formatDate(record.last_working_day, timezone ?? undefined)} />
          <Row label="Exit date" value={formatDate(record.exit_date, timezone ?? undefined)} />
          <Row label="Login" value={record.has_login ? "Enabled" : "No login"} />
        </dl>
        <div className="mt-4">
          <Badge tone={record.status === "active" ? "emerald" : "slate"}>
            {record.status?.replace(/_/g, " ")}
          </Badge>
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Financial</h2>
        {canAdmin ? (
          <>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Row
                label="Current salary"
                value={
                  salary.isPending
                    ? "Loading…"
                    : currentSalary
                      ? formatMoney(currentSalary.gross_monthly)
                      : "Not set"
                }
              />
              <Row
                label="Effective from"
                value={currentSalary ? formatDate(currentSalary.effective_from, timezone ?? undefined) : ""}
              />
              <Row label="Bank / payment reference" value={record.bank_account_ref} />
              <Row label="Payment mode" value={record.bank_account_ref ? "Bank transfer" : ""} />
            </dl>
            <p className="mt-4 text-xs text-slate-400">
              Salary components and the full structure live in the Salary tab.
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-500">
            Salary is visible to owners and admins only. Managers see the employment record, not pay.
          </p>
        )}
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Identity / Statutory</h2>
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Row label="Tax identifier (PAN)" value={record.tax_identifier} />
          <Row
            label="Provident fund"
            value={record.is_provident_fund_applicable ? "Applicable" : "Not applicable"}
          />
          <Row label="ESIC" value={record.is_esic_applicable ? "Applicable" : "Not applicable"} />
          <Row label="Employee ID" value={record.employee_code} />
        </dl>
        <p className="mt-4 text-xs text-slate-400">
          Government identifiers and proofs belong under the Documents tab.
        </p>
      </Card>

      <Card className="lg:col-span-2">
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Professional</h2>
        {skills.length === 0 && certifications.length === 0 && tags.length === 0 ? (
          <p className="text-sm text-slate-500">
            No skills recorded yet. Add them from Edit to describe what this person can do.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Skills</p>
              {skills.length === 0 ? (
                <p className="text-sm text-slate-400">Not provided</p>
              ) : (
                <ul className="space-y-1">
                  {skills.map((skill, index) => (
                    <li key={`${skill.name}-${index}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{skill.name}</span>
                      <span className="text-xs capitalize text-slate-500">{skill.level}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Certifications</p>
              {certifications.length === 0 ? (
                <p className="text-sm text-slate-400">Not provided</p>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700">
                  {certifications.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Tags</p>
              {tags.length === 0 ? (
                <p className="text-sm text-slate-400">Not provided</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {tags.map((tag) => (
                    <Badge key={tag} tone="sky">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
      <ReportingCard record={record} />
      {canAdmin ? <RoleCard record={record} /> : null}
    </div>
    </>
  );
}

/** The role a login carries. Owner/admin assign; manager is explicit. */
function RoleCard({ record }: { record: EmployeeFull }) {
  const queryClient = useQueryClient();
  const current = (record.user_role || "staff") as "owner" | "manager" | "staff";
  const [role, setRole] = useState<"owner" | "manager" | "staff">(current);
  const [saved, setSaved] = useState(false);

  const assign = useMutation({
    mutationFn: (next: "owner" | "manager" | "staff") => api.setEmployeeRole(record.id, next),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["employee", record.id] });
      void queryClient.invalidateQueries({ queryKey: ["employee-list"] });
    },
  });

  return (
    <Card>
      <h2 className="mb-4 text-sm font-semibold text-slate-900">Login &amp; role</h2>
      {!record.has_login ? (
        <p className="text-sm text-slate-500">
          This employee has no login. Add one via Edit to assign a role.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Current role</span>
            <Badge tone={current === "owner" ? "violet" : current === "manager" ? "sky" : "slate"}>
              {current === "owner" ? "Owner" : current === "manager" ? "Manager" : "Employee"}
            </Badge>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-600">Assign role</span>
            <select
              value={role}
              onChange={(event) => {
                setSaved(false);
                setRole(event.target.value as "owner" | "manager" | "staff");
              }}
              className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            >
              <option value="staff">Employee</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
          </label>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              disabled={assign.isPending || role === current}
              onClick={() => assign.mutate(role)}
            >
              {assign.isPending ? "Saving…" : "Save role"}
            </Button>
            {saved ? <span className="text-xs text-emerald-600">Saved.</span> : null}
          </div>
          <p className="text-xs text-slate-400">
            Owners administer the whole account. Managers manage the team they are
            assigned. Employees see only their own work. You cannot change your own
            role, and only an owner may grant the owner role.
          </p>
          {assign.isError ? (
            <p role="alert" className="text-sm text-rose-600">
              {assign.error instanceof ApiError ? assign.error.message : "Could not assign the role."}
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function ReportingCard({ record }: { record: EmployeeFull }) {
  const reportsTo = reportingManagerState(record);
  const manager = useQuery({
    queryKey: ["employee-manager", record.reporting_to],
    queryFn: ({ signal }) => api.employee(record.reporting_to!, signal),
    enabled: Boolean(record.reporting_to),
  });
  const reports = useQuery({
    queryKey: ["employee-reports", record.id],
    queryFn: ({ signal }) =>
      api.employeeList({ reporting_to: record.id, page_size: 50 }, signal),
  });
  const directs = (reports.data?.results ?? []).filter((p) => p.status !== "exited");
  const managerName = manager.data?.display_name || record.reporting_to_name;

  return (
    <Card className="lg:col-span-2">
      <h2 className="mb-4 text-sm font-semibold text-slate-900">Reporting</h2>
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Reports to</p>
          {reportsTo === "missing" ? (
            <p className="text-sm text-amber-600">
              No reporting manager set. Set one so leave can be routed for approval.
            </p>
          ) : reportsTo === "owner" ? (
            <p className="text-sm text-slate-600">
              Account owner — the root of the organization. The owner reports to no one.
            </p>
          ) : managerName ? (
            manager.data ? (
              <Link
                to={`/people/employees/${manager.data.id}`}
                className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50"
              >
                <Avatar name={manager.data.display_name} src={manager.data.photo} size={32} />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{manager.data.display_name}</span>
                  <span className="block text-xs text-slate-500">{manager.data.designation || "Not set"}</span>
                </span>
              </Link>
            ) : (
              <div className="inline-flex items-center gap-2 px-2 py-1.5">
                <Avatar name={managerName} size={32} />
                <span className="block text-sm font-medium text-slate-900">{managerName}</span>
              </div>
            )
          ) : manager.isPending ? (
            <Skeleton className="h-10 w-48" />
          ) : (
            <p className="text-sm text-slate-500">Manager record not found.</p>
          )}
        </div>
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            Direct reports {directs.length ? `(${directs.length})` : ""}
          </p>
          {reports.isPending ? (
            <Skeleton className="h-10 w-48" />
          ) : directs.length === 0 ? (
            <p className="text-sm text-slate-400">No direct reports.</p>
          ) : (
            <ul className="space-y-1">
              {directs.map((person) => (
                <li key={person.id}>
                  <Link
                    to={`/people/employees/${person.id}`}
                    className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-slate-50"
                  >
                    <Avatar name={person.display_name} src={person.photo} size={26} />
                    <span className="text-sm text-slate-800">{person.display_name}</span>
                    <span className="text-xs text-slate-400">{person.designation || ""}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function AttendanceTab({ employeeId, timezone }: { employeeId: string; timezone?: string | null }) {
  const { user } = useAuth();
  const canManage = isManagerOrAbove(user?.role);
  return (
    <div className="space-y-4">
      <AttendanceDetail employeeId={employeeId} timezone={timezone} />
      <AttendanceCorrections
        employeeId={employeeId}
        canManage={canManage}
        timezone={timezone}
      />
      <AttendanceCorrectionHistory employeeId={employeeId} />
    </div>
  );
}

function RosterTab({ employeeId }: { employeeId: string }) {
  const query = useQuery({
    queryKey: ["employee-shifts", employeeId],
    queryFn: ({ signal }) => api.shiftsPage({ employee: employeeId, page_size: 30 }, signal),
  });
  return (
    <RecordsTable
      loading={query.isPending}
      error={query.error}
      rows={query.data?.results ?? []}
      empty="No shifts rostered yet."
      columns={["Date", "Shift", "Hours", "Status"]}
      render={(row: Shift) => (
        <tr key={row.id} className="hover:bg-slate-50">
          <td className="px-4 py-2.5 text-sm text-slate-700">{formatDate(row.shift_date)}</td>
          <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">
            {row.start_time.slice(0, 5)}–{row.end_time.slice(0, 5)}
            {row.spans_midnight ? <span className="ml-2 text-xs text-sky-600">overnight</span> : null}
          </td>
          <td className="px-4 py-2.5 text-sm tabular-nums text-slate-700">{formatMinutes(row.duration_minutes)}</td>
          <td className="px-4 py-2.5">
            <Badge tone={SHIFT_TONE[row.status ?? "draft"]}>{SHIFT_LABEL[row.status ?? "draft"]}</Badge>
          </td>
        </tr>
      )}
    />
  );
}

function LeaveTab({ employeeId }: { employeeId: string }) {
  const requests = useQuery({
    queryKey: ["employee-leave", employeeId],
    queryFn: ({ signal }) => api.leaveRequests({ employee: employeeId, page_size: 30 }, signal),
  });
  const balances = useQuery({
    queryKey: ["employee-leave-balances", employeeId],
    queryFn: ({ signal }) => api.leaveBalances({ employee: employeeId }, signal),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-1">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">Balances</h2>
        {balances.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : (balances.data?.results ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No balances yet.</p>
        ) : (
          <ul className="space-y-2">
            {(balances.data?.results ?? []).map((balance: LeaveBalance) => (
              <li key={balance.id} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{balance.leave_type_name ?? "Leave"}</span>
                <span className="tabular-nums text-slate-900">{Number(balance.available_days)} left</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div className="lg:col-span-2">
        <RecordsTable
          loading={requests.isPending}
          error={requests.error}
          rows={requests.data?.results ?? []}
          empty="No leave requests yet."
          columns={["Type", "From", "To", "Days", "Status"]}
          render={(row: LeaveRequest) => (
            <tr key={row.id} className="hover:bg-slate-50">
              <td className="px-4 py-2.5 text-sm text-slate-700">{row.leave_type_name}</td>
              <td className="px-4 py-2.5 text-sm text-slate-600">{formatDate(row.from_date)}</td>
              <td className="px-4 py-2.5 text-sm text-slate-600">{formatDate(row.to_date)}</td>
              <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{row.days}</td>
              <td className="px-4 py-2.5">
                <Badge tone={LEAVE_TONE[row.status ?? "pending"]}>{LEAVE_LABEL[row.status ?? "pending"]}</Badge>
              </td>
            </tr>
          )}
        />
      </div>
    </div>
  );
}

function SalaryTab({
  employeeId,
  canRevise,
  currency,
}: {
  employeeId: string;
  canRevise: boolean;
  currency?: string | null;
}) {
  const queryClient = useQueryClient();
  const [revising, setRevising] = useState<SalaryStructure | null>(null);
  const query = useQuery({
    queryKey: ["employee-salary", employeeId],
    queryFn: ({ signal }) => api.salaryStructures({ employee: employeeId, page_size: 20 }, signal),
  });
  const components = useQuery({
    queryKey: ["salary-components", 100],
    queryFn: ({ signal }) => api.salaryComponents(signal),
  });
  const revise = useMutation({
    mutationFn: (values: {
      employee: string;
      name?: string;
      effective_from: string;
      gross_monthly: string;
      lines: Array<{
        component: string;
        calculation_mode?: "amount" | "percent_of_gross";
        amount?: string | null;
        percent_of_gross?: string | null;
      }>;
    }) =>
      api.createSalaryStructure({
        employee: values.employee,
        name: values.name,
        effective_from: values.effective_from,
        gross_monthly: {
          amount: values.gross_monthly,
          currency: currency ?? "INR",
        },
        lines: values.lines,
      }),
    onSuccess: () => {
      setRevising(null);
      void queryClient.invalidateQueries({ queryKey: ["employee-salary", employeeId] });
      void queryClient.invalidateQueries({ queryKey: ["salary-structures"] });
    },
  });
  const structures = query.data?.results ?? [];

  if (query.isPending) return <Skeleton className="h-32 w-full" />;
  if (query.isError)
    return <EmptyState title="Could not load salary" body={messageOf(query.error)} />;
  if (structures.length === 0)
    return (
      <EmptyState
        title="No salary structure"
        body="Set this employee's compensation under Salary. Payroll reads it directly."
      />
    );

  return (
    <div className="space-y-4">
      {structures.map((structure: SalaryStructure) => (
        <Card key={structure.id}>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">{structure.name}</h2>
              <p className="text-xs text-slate-500">
                Effective {formatDate(structure.effective_from)}
                {structure.effective_to ? ` ?+' ${formatDate(structure.effective_to)}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canRevise && structure.is_current ? (
                <Button variant="primary" onClick={() => setRevising(structure)}>
                  <Pencil size={14} /> Revise Salary
                </Button>
              ) : null}
              <div className="text-right">
                <p className="text-sm font-semibold tabular-nums text-slate-900">
                  {formatMoney({
                    amount: structureTotals(structure).gross.toFixed(2),
                    currency: currency ?? "INR",
                  })}
                </p>
                {structure.is_current ? <Badge tone="emerald">Current</Badge> : null}
              </div>
            </div>
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="py-1">Component</th>
                <th className="py-1">Kind</th>
                <th className="py-1 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(structure.lines ?? []).map((line) => (
                <tr key={line.id}>
                  <td className="py-2 text-sm text-slate-700">{line.component_name}</td>
                  <td className="py-2 text-sm capitalize text-slate-500">{line.component_kind}</td>
                  <td className="py-2 text-right text-sm tabular-nums text-slate-700">
                    {line.amount != null
                      ? formatMoney({ amount: line.amount, currency: "INR" })
                      : line.percent_of_gross != null
                        ? `${line.percent_of_gross}%`
                        : "??"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      {revising ? (
        <AddStructure
          employees={[]}
          components={components.data?.results ?? []}
          pending={revise.isPending}
          error={revise.error instanceof ApiError ? revise.error : null}
          initial={revising}
          employeeName={revising.employee_name}
          currency={currency}
          title="Revise salary"
          submitLabel="Save revision"
          closeOnSubmit={false}
          onSubmit={(values) => revise.mutate(values)}
          onClose={() => {
            setRevising(null);
            revise.reset();
          }}
        />
      ) : null}
    </div>
  );
}
function PayrollTab({ employeeId, timezone }: { employeeId: string; timezone?: string | null }) {
  const query = useQuery({
    queryKey: ["employee-payslips", employeeId],
    queryFn: ({ signal }) => api.payslips({ employee: employeeId, page_size: 24 }, signal),
  });
  return (
    <RecordsTable
      loading={query.isPending}
      error={query.error}
      rows={query.data?.results ?? []}
      empty="No payslips yet."
      columns={["Run", "Period", "Days", "Gross", "Net", "Status"]}
      render={(row: PayrollItem) => (
        <tr key={row.id} className="hover:bg-slate-50">
          <td className="px-4 py-2.5 text-sm text-slate-700">{row.run_number}</td>
          <td className="px-4 py-2.5 text-sm text-slate-600">
            {formatDate(row.period_start, timezone ?? undefined)} → {formatDate(row.period_end, timezone ?? undefined)}
          </td>
          <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{formatDayCount(row.days_worked)}</td>
          <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{row.gross?.amount}</td>
          <td className="px-4 py-2.5 text-sm tabular-nums font-medium text-slate-900">{row.net_pay?.amount}</td>
          <td className="px-4 py-2.5">
            <Badge tone="slate">{row.status}</Badge>
          </td>
        </tr>
      )}
    />
  );
}

function DocumentsTab({
  employeeId,
  canManage,
  canAdmin,
}: {
  employeeId: string;
  canManage: boolean;
  canAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const documents = useQuery({
    queryKey: ["employee-documents", employeeId],
    queryFn: ({ signal }) => api.employeeDocuments({ employee: employeeId, page_size: 50 }, signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["employee-documents", employeeId] });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEmployeeDocument(id),
    onSuccess: invalidate,
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.updateEmployeeDocument(id, { status: status as EmployeeDocument["status"] }),
    onSuccess: invalidate,
  });

  const download = async (document: EmployeeDocument) => {
    const blob = await api.downloadEmployeeDocument(document.id);
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = document.file_name || document.name;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Identity, employment and financial documents. Sensitive files are permission-controlled.
        </p>
        {canManage && !adding ? (
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus size={15} /> Upload
          </Button>
        ) : null}
      </div>

      {adding ? (
        <UploadDocumentForm
          employeeId={employeeId}
          canAdmin={canAdmin}
          onDone={() => {
            setAdding(false);
            invalidate();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {documents.isPending ? (
          <TableSkeleton rows={3} />
        ) : documents.isError ? (
          <EmptyState title="Could not load documents" body={messageOf(documents.error)} />
        ) : (documents.data?.results ?? []).length === 0 ? (
          <EmptyState
            title="No documents"
            body="Upload identity and employment documents to build this employee's file."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Document</th>
                <th className="px-4 py-2.5">Category</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Expiry</th>
                <th className="px-4 py-2.5 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(documents.data?.results ?? []).map((document) => (
                <tr key={document.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-slate-900">{document.name}</p>
                    <p className="text-xs text-slate-500">
                      {document.file_name} · {document.visibility_display}
                    </p>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{document.category_display}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={document.status === "verified" ? "emerald" : document.status === "rejected" ? "rose" : "slate"}>
                      {document.status_display}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{formatDate(document.expiry_date)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" title="Download" onClick={() => download(document)}>
                        <Download size={14} />
                      </Button>
                      {canManage && document.status !== "verified" ? (
                        <Button
                          variant="ghost"
                          title="Mark verified"
                          disabled={setStatus.isPending}
                          onClick={() => setStatus.mutate({ id: document.id, status: "verified" })}
                        >
                          Verify
                        </Button>
                      ) : null}
                      {canManage ? (
                        <Button
                          variant="ghost"
                          title="Delete"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(document.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function UploadDocumentForm({
  employeeId,
  canAdmin,
  onDone,
  onCancel,
}: {
  employeeId: string;
  canAdmin: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<DocumentCategory>("identity");
  const [visibility, setVisibility] = useState<DocumentVisibility>("standard");
  const [expiry, setExpiry] = useState("");

  const upload = useMutation({
    mutationFn: () =>
      api.uploadEmployeeDocument({
        employee: employeeId,
        name: name.trim() || file?.name || "Document",
        category,
        visibility,
        expiry_date: expiry || null,
        file: file!,
      }),
    onSuccess: onDone,
  });

  return (
    <Card className="mb-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!file) return;
          upload.mutate();
        }}
        className="grid gap-3"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-600">Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Defaults to the file name"
              className={inputClass}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-600">File</span>
            <input
              type="file"
              required
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="w-full text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-600">Category</span>
            <select value={category} onChange={(event) => setCategory(event.target.value as DocumentCategory)} className={inputClass}>
              <option value="identity">Identity</option>
              <option value="employment">Employment</option>
              <option value="education">Education / Skills</option>
              <option value="financial">Financial</option>
              <option value="address">Address / Emergency</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-600">Visibility</span>
            <select value={visibility} onChange={(event) => setVisibility(event.target.value as DocumentVisibility)} className={inputClass}>
              <option value="standard">Employee and managers</option>
              <option value="restricted">Managers only</option>
              <option value="private">Owner / admin only</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-600">Expiry date</span>
            <input type="date" value={expiry} onChange={(event) => setExpiry(event.target.value)} className={inputClass} />
          </label>
        </div>
        {canAdmin ? null : <p className="text-xs text-slate-400">Uploads are limited to the visibility above.</p>}
        {upload.error instanceof ApiError ? (
          <p role="alert" className="text-sm text-rose-600">
            {upload.error.message}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={upload.isPending || !file}>
            Upload
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TimelineTab({ record, timezone }: { record: EmployeeFull; timezone?: string | null }) {
  const attendance = useQuery({
    queryKey: ["employee-attendance", record.id],
    queryFn: ({ signal }) => api.attendancePage({ employee: record.id, page_size: 15 }, signal),
  });
  const shifts = useQuery({
    queryKey: ["employee-shifts", record.id],
    queryFn: ({ signal }) => api.shiftsPage({ employee: record.id, page_size: 15 }, signal),
  });
  const leave = useQuery({
    queryKey: ["employee-leave", record.id],
    queryFn: ({ signal }) => api.leaveRequests({ employee: record.id, page_size: 15 }, signal),
  });
  const salary = useQuery({
    queryKey: ["employee-salary", record.id],
    queryFn: ({ signal }) => api.salaryStructures({ employee: record.id, page_size: 15 }, signal),
  });

  if (attendance.isPending || shifts.isPending || leave.isPending || salary.isPending) {
    return <Skeleton className="h-32 w-full" />;
  }

  const events: Array<{ date: string; label: string; detail: string; tone: string }> = [];

  // Real employment lifecycle, read straight off the record. Nothing here is
  // invented: an unset date simply has no event.
  const lifecycle: Array<[string | null | undefined, string, string, string]> = [
    [record.joining_date, "Joined", record.designation || "Employee", "slate"],
    [record.probation_end_date, "Probation ended", "", "violet"],
    [record.confirmation_date, "Confirmed", "", "emerald"],
    [record.resignation_date, "Resignation", record.exit_reason || "", "amber"],
    [record.last_working_day, "Last working day", "", "amber"],
    [record.exit_date, "Exit", record.exit_reason || "", "rose"],
  ];
  for (const [when, label, detail, tone] of lifecycle) {
    if (!when) continue;
    events.push({ date: when, label, detail, tone });
  }
  for (const structure of salary.data?.results ?? []) {
    events.push({
      date: structure.effective_from,
      label: structure.is_current ? "Salary · current" : "Salary change",
      detail: formatMoney(structure.gross_monthly),
      tone: "emerald",
    });
  }
  for (const row of attendance.data?.results ?? []) {
    events.push({
      date: row.work_date ?? "",
      label: `Attendance · ${ATTENDANCE_LABEL[row.status ?? "present"]}`,
      detail: row.clock_in_at ? `In ${formatDateTime(row.clock_in_at, timezone ?? undefined)}` : "No check-in",
      tone: "slate",
    });
  }
  for (const row of shifts.data?.results ?? []) {
    events.push({
      date: row.shift_date,
      label: `Shift · ${SHIFT_LABEL[row.status ?? "draft"]}`,
      detail: `${row.start_time.slice(0, 5)}–${row.end_time.slice(0, 5)}`,
      tone: "sky",
    });
  }
  for (const row of leave.data?.results ?? []) {
    events.push({
      date: row.from_date,
      label: `Leave · ${LEAVE_LABEL[row.status ?? "pending"]}`,
      detail: `${row.leave_type_name} · ${row.days} day(s)`,
      tone: "amber",
    });
  }
  events.sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <Card>
      {events.length === 0 ? (
        <EmptyState title="Nothing recorded yet" body="Attendance, shifts and leave will appear here." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {events.slice(0, 30).map((event, index) => (
            <li key={index} className="flex items-start gap-3 py-2.5">
              <span className="w-24 shrink-0 text-xs tabular-nums text-slate-500">
                {formatDate(event.date, timezone ?? undefined)}
              </span>
              <Badge tone={event.tone as "slate" | "sky" | "amber" | "emerald" | "violet" | "rose"}>
                {event.label}
              </Badge>
              <span className="text-sm text-slate-600">{event.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecordsTable<T extends { id: string }>({
  loading,
  error,
  rows,
  empty,
  columns,
  render,
}: {
  loading: boolean;
  error: unknown;
  rows: T[];
  empty: string;
  columns: string[];
  render: (row: T) => React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        <TableSkeleton rows={4} />
      </div>
    );
  }
  if (error) {
    return <EmptyState title="Could not load records" body={messageOf(error)} />;
  }
  if (rows.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        <EmptyState title="Nothing here yet" body={empty} />
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            {columns.map((column) => (
              <th key={column} className="px-4 py-2.5">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{rows.map(render)}</tbody>
      </table>
    </div>
  );
}

function Row({ label, value, capitalize }: { label: string; value?: string | null; capitalize?: boolean }) {
  const provided = value != null && String(value).trim() !== "";
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={`mt-0.5 text-sm ${provided ? "text-slate-800" : "text-slate-400"} ${
          capitalize ? "capitalize" : ""
        }`}
      >
        {provided ? value : "Not provided"}
      </dd>
    </div>
  );
}

function messageOf(error: unknown): string {
  return error instanceof ApiError ? error.message : "Please try again.";
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
